import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { ApiMasterService } from '../../../../../../core/services/api-master.service';
import { ToastService } from 'angular-toastify';
import { ModalRegisterParkingFormAddComponent } from '../modal-register-parking-form-add/modal-register-parking-form-add.component';

import {
  Blank,
  FeeType,
  Providers,
} from '../../../../../../core/models/information.model';
import {
  CityParkingAddressSelectResponse,
  CityParkingRegisterDetailMemberGroupTable,
  CityParkingRegisterGroupByIdResponse,
  CityParkingRegisterMemberGroupPayload,
} from '../../../../../../core/models/register-parking';
import { ApiFormService } from '../../../../../../core/services/api-form.service';
import { ModalFullltaxFormComponent } from '../../../other/modal-fullltax-form/modal-fullltax-form.component';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { CityParkingCustomerInfoResponse } from '../../../../../../core/models/customer.model';
import { ParkingVehicleTypeResponse } from '../../../../../../core/models/vehicle.model';
import { CookieService } from 'ngx-cookie-service';
import {
  CityParkingInvoiceCustomerIdResponse,
  InvoiceResponse,
} from '../../../../../../core/models/invoice.model';
import { ClientAccessScopeService } from '../../../../../../core/services/client-access-scope.service';
import { VatSettingService } from '../../../../../../core/services/vat-setting.service';
import { ClientIInfo } from '../../../../../../core/models/parking.model';
import {
  calculateVatLineAmounts,
  VatLineAmounts,
} from '../../../../../../core/utils/vat-amount.util';

@Component({
  selector: 'app-modal-register-parking-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalRegisterParkingFormAddComponent,
    ModalFullltaxFormComponent,
    PssConfirmModalComponent,
  ],
  templateUrl: './modal-register-parking-form.component.html',
  styleUrl: './modal-register-parking-form.component.css',
})
export class ModalRegisterParkingFormComponent implements OnInit {
  @Input() siteId = 0;
  @Input() menuId = 0;
  @Input() groupId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private static readonly CUSTOMER_OPTIONS_PREVIEW = 20;

  // Master Data
  locations: Providers[] = [];
  customers: CityParkingCustomerInfoResponse[] = []; // List of customers
  paymentTypes: FeeType[] = [];
  carTypes: ParkingVehicleTypeResponse[] = [
    {
      id: 1,
      typeName: 'รถยนต์',
      icon: 'directions_car', // ชื่อไอคอนรถยนต์
    },
    {
      id: 2,
      typeName: 'มอเตอร์ไซค์',
      icon: 'two_wheeler', // ชื่อไอคอนมอเตอร์ไซค์
    },
  ];
  description: string = '';
  userId: number = 0;
  receiptNo: string = '';
  today: string = new Date().toISOString().split('T')[0];

  // Form State
  selectedLocationId: number | null = null;
  selectedClientId: number = 0;
  clients: ClientIInfo[] = [];
  selectedCustomerId: number | null = null;
  previousLocationId: number | null = null;
  previousCustomerId: number | null = null;
  mode: 'new' | 'renew' = 'new';
  selectedLineMemberId: number | null = null;
  private pendingInvoiceId: number | null = null;

  // Tax Invoice
  taxInvoiceNeeded: boolean = true;
  taxs: CityParkingAddressSelectResponse[] = [];
  selectedTaxAddressId: number = 0;
  addresses: any[] = []; // Addresses for selected customer

  // Payment
  selectedPaymentTypeId: number | null = null;
  selectedBankId: number | null = null;
  banks: Blank[] = [];
  delayed: boolean = false;
  dueDate: string = '';
  selectedInvoiceId: number | null = null;
  invoices: InvoiceResponse[] = [];

  // API
  cityparkingRegisterMemberGroupPayload: CityParkingRegisterMemberGroupPayload =
    {} as CityParkingRegisterMemberGroupPayload;

  // Table Data
  selectedItems: CityParkingRegisterDetailMemberGroupTable[] = [];

  // Modal State
  isAddModalOpen: boolean = false;
  isFulltaxModalOpen: boolean = false;
  isConfirmModalOpen: boolean = false; // Confirm Modal State
  selectedVehicleTypeId: number | null = null;

  subTotal: number = 0;
  vatAmount: number = 0;
  total: number = 0;

  isLoading: boolean = false;

  // Custom Dropdown State
  openDropdown: string | null = null;
  customerSearchText = '';
  isCustomerDropdownOpen = false;
  /** ชื่อบริษัทผู้เช่าจากฉบับร่าง — ใช้เมื่อหา customer ในรายการลานไม่เจอ */
  draftClientName = '';

  private readonly clientScope = inject(ClientAccessScopeService);
  readonly vatSetting = inject(VatSettingService);

  constructor(
    private apiService: ApiService,
    private apiMasterService: ApiMasterService,
    private apiFormService: ApiFormService,
    private toast: ToastService,
    private cookieService: CookieService,
  ) { }

  // ================= Normal Functions =================

  /**
   * ตรวจสอบว่าฟิลด์สถานที่โดนล็อคอยู่หรือไม่ (ในกรณีที่หน้าหลักส่งค่า siteId มาให้ หรือโหมดแก้ไข)
   */
  get locationLocked(): boolean {
    return this.siteId > 0 || this.isEditMode;
  }

  get isEditMode(): boolean {
    return this.groupId > 0;
  }

  get formTitle(): string {
    return this.isEditMode ? 'แก้ไขฉบับร่าง' : 'ลงทะเบียนจอดรถ';
  }

  get formSubtitle(): string {
    return this.isEditMode
      ? 'แก้ไขข้อมูลฉบับร่างก่อนยืนยันชำระเงิน'
      : 'จัดการการลงทะเบียนสมาชิกรายเดือนและต่ออายุ';
  }

  /**
   * บริษัทผู้เช่ามาจาก city_parking_customer.client_id ของลูกค้าที่เลือก (ไม่ให้เลือกเอง)
   */
  get selectedClientName(): string {
    if (this.selectedClientId <= 0) return '';
    const customer = this.customers.find((c) => c.id === this.selectedCustomerId);
    if (customer?.clientId === this.selectedClientId && customer.clientName) {
      return customer.clientName;
    }
    return this.clients.find((c) => c.id === this.selectedClientId)?.name
      || this.draftClientName
      || `บริษัทผู้เช่า #${this.selectedClientId}`;
  }

  /** ลูกค้าที่เลือกแล้วแต่ยังไม่ได้ผูกบริษัทผู้เช่าในหน้าจัดการลูกค้า */
  get isSelectedCustomerUnlinked(): boolean {
    return !!this.selectedCustomerId && this.selectedClientId <= 0;
  }

  /**
   * ลูกค้าของลานนี้ — ถ้าผู้ใช้ถูกจำกัดสิทธิ์บริษัทผู้เช่า แสดงเฉพาะลูกค้าของบริษัทที่มีสิทธิ์
   */
  private get availableCustomers(): CityParkingCustomerInfoResponse[] {
    if (!this.clientScope.isRestricted()) return this.customers;
    const allowed = new Set(this.clients.map((c) => c.id));
    return this.customers.filter((c) => !!c.clientId && allowed.has(c.clientId));
  }

  get isCustomerFieldLocked(): boolean {
    return this.isEditMode;
  }

  get isModeLocked(): boolean {
    return this.isEditMode;
  }

  /**
   * ข้อความแสดงจำนวนรายการที่จะยืนยันใน Modal ยืนยันการบันทึกข้อมูล
   */
  get confirmHighlight(): string {
    return `${this.selectedItems.length} รายการ`;
  }

  /**
   * ข้อความยอดเงินรวมส่วนท้ายที่จะแสดงใน Modal ยืนยันการบันทึกข้อมูล
   */
  get confirmMessageSuffix(): string {
    return ` ยอดรวม ${this.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท หรือไม่?`;
  }

  /**
   * ดึงข้อมูลสถานที่แปลงเป็นตัวเลือกสำหรับแสดงผลใน Dropdown
   */
  get locationOptions() {
    return this.locations.map(l => ({ label: l.name, value: l.id }));
  }

  /**
   * ดึงข้อมูลลูกค้าและกรองตามตัวหนังสือที่ใช้พิมพ์ค้นหา
   */
  get customerFilterOptions(): { label: string; value: number; clientName: string }[] {
    const sorted = [...this.availableCustomers].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', 'th'),
    );
    const q = this.customerSearchText.trim().toLowerCase();
    const filtered = !q
      ? sorted.slice(0, ModalRegisterParkingFormComponent.CUSTOMER_OPTIONS_PREVIEW)
      : sorted.filter((c) => {
        const name = (c.name ?? '').toLowerCase();
        const phone = (c.phone ?? '').toLowerCase();
        const clientName = (c.clientName ?? '').toLowerCase();
        return name.includes(q) || phone.includes(q) || clientName.includes(q);
      });

    return filtered.map((c) => ({
      label: `${c.name} (${c.phone})`,
      value: c.id,
      clientName: c.clientName ?? '',
    }));
  }

  /**
   * ดึงข้อมูลใบแจ้งหนี้เพื่อใช้ใน Dropdown
   */
  get invoiceOptions() {
    return this.invoices.map(i => ({ label: i.invoiceNumber, value: i.id }));
  }

  /**
   * ดึงข้อมูลประเภทการจ่ายเงินเพื่อใช้ใน Dropdown
   */
  get paymentTypeOptions() {
    return this.paymentTypes.map(p => ({ label: p.name, value: p.id }));
  }

  /**
   * ดึงข้อมูลธนาคารเพื่อใช้ใน Dropdown
   */
  get bankOptions() {
    return this.banks.map(b => ({ label: `${b.name} (${b.accountNumber})`, value: b.id }));
  }

  /**
   * ดึงข้อมูลที่อยู่ออกใบกำกับภาษีเพื่อใช้ใน Dropdown
   */
  get taxOptions() {
    return this.taxs.map(t => ({ label: `${t.taxId} - ${t.name}`, value: t.id }));
  }

  /**
   * เปิดหรือปิดเมนู Dropdown ในหน้าจอตาม key ที่กำหนด
   */
  toggleDropdown(dropdown: string, event: Event) {
    event.stopPropagation();
    if (dropdown === 'locations' && this.locationLocked) return;
    if (this.selectedItems.length > 0 && dropdown !== 'taxs') return;
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  /**
   * ปิด dropdown ทุกอันในหน้าจอ
   */
  closeDropdowns() {
    this.openDropdown = null;
    this.closeCustomerDropdown();
  }

  /**
   * เปิด dropdown ของช่องเลือกลูกค้า
   */
  openCustomerDropdown(): void {
    if (this.selectedItems.length > 0) return;
    this.isCustomerDropdownOpen = true;
    this.openDropdown = null;
  }

  /**
   * ปิด dropdown ของช่องเลือกลูกค้า
   */
  closeCustomerDropdown(): void {
    this.isCustomerDropdownOpen = false;
    if (this.selectedCustomerId) {
      const selected = this.customers.find((c) => c.id === this.selectedCustomerId);
      if (selected) {
        this.customerSearchText = `${selected.name} (${selected.phone})`;
      }
    }
  }

  /**
   * ทำงานเมื่อมีการพิมพ์ค้นหาชื่อลูกค้า
   */
  onCustomerSearchInput(): void {
    if (this.selectedItems.length > 0) return;
    this.isCustomerDropdownOpen = true;
    this.openDropdown = null;
  }

  /**
   * เลือกตัวเลือกของลูกค้าจาก Dropdown
   */
  selectCustomerOption(customerId: number): void {
    if (this.selectedItems.length > 0) return;
    this.selectedCustomerId = customerId;
    const selected = this.customers.find((c) => c.id === customerId);
    if (selected) {
      this.customerSearchText = `${selected.name} (${selected.phone})`;
    }
    this.selectedClientId = selected?.clientId ?? 0;
    if (!this.selectedClientId) {
      this.toast.warn('ลูกค้ารายนี้ยังไม่ได้ผูกบริษัทผู้เช่า กรุณากำหนดที่หน้าจัดการลูกค้าก่อน');
    }
    this.isCustomerDropdownOpen = false;
    this.onCustomerChange();
  }

  /**
   * เคลียร์ค่าลูกค้าที่เลือกไว้
   */
  clearCustomerSelection(): void {
    if (this.selectedItems.length > 0) return;
    this.selectedCustomerId = null;
    this.selectedClientId = 0;
    this.customerSearchText = '';
    this.isCustomerDropdownOpen = false;
    this.onCustomerChange();
  }

  /**
   * เลือกตัวเลือกของ dropdown แบบเลือกทั่วไป
   */
  selectOption(dropdown: string, value: any) {
    if (dropdown === 'locations' && this.locationLocked) return;
    if (this.selectedItems.length > 0 && dropdown !== 'taxs') return;

    switch (dropdown) {
      case 'locations':
        this.selectedLocationId = value;
        this.onLocationChange();
        break;
      case 'invoices':
        this.selectedInvoiceId = value;
        break;
      case 'paymentTypes':
        this.selectedPaymentTypeId = value;
        break;
      case 'banks':
        this.selectedBankId = value;
        break;
      case 'taxs':
        this.selectedTaxAddressId = value;
        break;
    }
    this.closeDropdowns();
  }

  /**
   * ดึงชื่อหรือข้อความที่ต้องการแสดงใน Dropdown สำหรับค่าที่เลือกอยู่
   */
  getOptionName(dropdown: string, value: any): string {
    if (value === null || value === undefined) return '';

    switch (dropdown) {
      case 'locations':
        return this.locations.find(l => l.id === value)?.name || '';
      case 'customers':
        const cust = this.customers.find(c => c.id === value);
        return cust ? `${cust.name} (${cust.phone})` : '';
      case 'invoices':
        return this.invoices.find(i => i.id === value)?.invoiceNumber || '';
      case 'paymentTypes':
        return this.paymentTypes.find(p => p.id === value)?.name || '';
      case 'banks':
        const bank = this.banks.find(b => b.id === value);
        return bank ? `${bank.name} (${bank.accountNumber})` : '';
      case 'taxs':
        const tax = this.taxs.find(t => t.id === value);
        return tax ? `${tax.taxId} - ${tax.name}` : '';
      default:
        return '';
    }
  }

  /**
   * ฟังก์ชันเรียกใช้งานเมื่อเข้าสู่ Component ครั้งแรก
   */
  ngOnInit(): void {
    this.applySiteDefault();
    this.loadBanks();
    this.loadMasterData();
    this.getVehicleTypes();
    this.getPaymentTypes();
    this.getCityParkingCustomerList();

    const userIdFromSession = sessionStorage.getItem('user_id');
    this.userId =
      parseInt(this.cookieService.get('user_id')) ||
      (userIdFromSession ? JSON.parse(userIdFromSession) : 0);

    this.dueDate = new Date().toISOString().split('T')[0];
    this.loadClients();

    if (this.isEditMode) {
      this.loadDraftForEdit();
    }
  }

  /**
   * ทำงานเมื่อเปลี่ยนโหมดระหว่างการลงทะเบียนใหม่และต่ออายุ
   */
  onModeChange(newMode: 'new' | 'renew'): void {
    if (newMode === 'renew' && this.selectedClientId <= 0) {
      this.toast.error('กรุณาเลือกลูกค้าที่ผูกบริษัทผู้เช่าแล้วก่อนเลือกต่ออายุ');
      setTimeout(() => {
        this.mode = 'new';
      });
    }
  }

  /**
   * ดึงรายการ Client ตามสถานที่และผู้ใช้งาน
   */
  private loadClients(): void {
    const siteId = this.selectedLocationId || this.siteId;
    if (!siteId || !this.userId) {
      this.clients = [];
      return;
    }

    this.clientScope.load(siteId, this.userId).subscribe({
      next: (state) => {
        this.clients = state.clients;
      },
      error: () => {
        this.clients = [];
      },
    });
  }

  /**
   * กำหนดค่าเริ่มต้นของสถานที่ตามที่ถูกส่งมาจาก parent component
   */
  private applySiteDefault(): void {
    if (this.siteId > 0) {
      this.selectedLocationId = this.siteId;
      this.previousLocationId = this.siteId;
    }
  }

  /**
   * ทำงานเมื่อทำการเลือกประเภทการชำระเงิน
   */
  onChangePayment(event: any) {
    this.selectedPaymentTypeId = event.target.value;
  }

  /**
   * ทำงานเมื่อสถานที่เปลี่ยน
   */
  onLocationChange() {
    this.loadBanks();
    this.checkLocationAndCustomerChange();
  }

  /**
   * ทำงานเมื่อลูกค้าเปลี่ยน
   */
  onCustomerChange() {
    this.addresses = [];
    this.selectedLineMemberId = 0;
    this.selectedTaxAddressId = 0;

    if (this.selectedCustomerId) {
      this.isLoading = true;
      this.getTax();
    }
    this.checkLocationAndCustomerChange();
  }

  /**
   * ตรวจสอบว่าสถานที่และลูกค้าได้มีการเลือกหรือเปลี่ยนไปจากตัวแปรเดิมเพื่อทำการดึงใบแจ้งหนี้ใหม่
   */
  checkLocationAndCustomerChange() {
    if (this.selectedLocationId && this.selectedCustomerId) {
      if (
        this.selectedLocationId !== this.previousLocationId ||
        this.selectedCustomerId !== this.previousCustomerId
      ) {
        this.previousLocationId = this.selectedLocationId;
        this.previousCustomerId = this.selectedCustomerId;
        this.onLocationAndCustomerReady();
      }
    }
  }

  /**
   * ฟังก์ชันเรียกเมื่อทำการเลือกสถานที่และลูกค้าครบเรียบร้อย
   */
  onLocationAndCustomerReady() {
    console.log('Location and Customer are selected and changed:');
    console.log('Location ID:', this.selectedLocationId);
    console.log('Customer ID:', this.selectedCustomerId);
    this.getInvoice();
  }

  /**
   * ปิด Modal ลงทะเบียนจอดรถ
   */
  onClose(): void {
    this.close.emit();
  }

  /**
   * เรียกทำงานเมื่อบันทึกผลการลงทะเบียนสำเร็จและปิดฟอร์ม
   */
  onSaved(): void {
    this.saved.emit();
    this.onClose();
  }

  /**
   * เลือกประเภทประเภทรถที่จะลงทะเบียน (เช่น รถยนต์ หรือ มอเตอร์ไซค์)
   */
  selectCarType(id: number) {
    if (this.selectedItems.length > 0) return; // ห้ามแก้ไขถ้ามีรายการอยู่แล้ว
    this.selectedVehicleTypeId = id;
  }

  /**
   * ฟังก์ชันรับข้อมูลทะเบียนที่เพิ่มสำเร็จมาจาก Modal Add และใส่ลงในรายการที่จะลงทะเบียนจอดรถ
   */
  handleModalSave(item: CityParkingRegisterDetailMemberGroupTable) {
    item.siteId = this.selectedLocationId || 0;
    this.selectedItems.push(item);
    this.calculateTotals();
  }

  /**
   * ตรวจสอบความถูกต้องของข้อมูลทั้งหมดในฟอร์มก่อนทำการบันทึกข้อมูล
   */
  checkValue(): boolean {
    if (!this.selectedLocationId || !this.selectedCustomerId) {
      this.toast.error('กรุณาเลือกสถานที่และลูกค้าก่อน');
      return true;
    }
    if (this.selectedClientId <= 0) {
      this.toast.error('ลูกค้ารายนี้ยังไม่ได้ผูกบริษัทผู้เช่า กรุณากำหนดที่หน้าจัดการลูกค้าก่อน');
      return true;
    }
    if (
      this.selectedVehicleTypeId === 0 ||
      this.selectedVehicleTypeId === null
    ) {
      this.toast.error('กรุณาเลือกประเภทของรถก่อน');
      return true;
    }

    if (!this.selectedPaymentTypeId) {
      this.toast.error('กรุณาเลือกประเภทการชำระเงินก่อน');
      return true;
    } else if (
      this.selectedPaymentTypeId === 3 &&
      (!this.selectedBankId || this.selectedBankId === null)
    ) {
      this.toast.error('กรุณาเลือกธนาคารก่อน');
      return true;
    }

    if (this.delayed) {
      if (!this.dueDate) {
        this.toast.error('กรุณาเลือกวันครบกำหนดชำระ');
        return true;
      }
      if (this.checkDueDateDelay() && !this.receiptNo) {
        this.toast.error('กรุณากรอกเลขที่ใบเสร็จ');
        return true;
      }
    }

    return false;
  }

  /**
   * เปิด Modal เพิ่มข้อมูลรายละเอียดการจอดรถ
   */
  openAddModal() {
    if (this.checkValue()) {
      return;
    }
    this.isAddModalOpen = true;
  }

  /**
   * เปิด Modal เพิ่มข้อมูลที่อยู่ออกใบกำกับภาษี
   */
  openFulltaxModal() {
    this.isFulltaxModalOpen = true;
  }

  /**
   * ปิด Modal เพิ่มข้อมูลรายละเอียดการจอดรถ
   */
  closeAddModal() {
    this.isAddModalOpen = false;
  }

  /**
   * ปิด Modal เพิ่มข้อมูลที่อยู่ออกใบกำกับภาษี
   */
  closeFulltaxModal() {
    this.isFulltaxModalOpen = false;
  }

  /**
   * ทำการดึงรายการที่อยู่ออกใบกำกับภาษีใหม่หลังจากบันทึกเสร็จใน Modal Fulltax
   */
  saveFulltaxModal(event: any) {
    this.isFulltaxModalOpen = false;
    this.getTax();
  }

  /**
   * ลบรายการรายละเอียดการจอดรถที่เลือกออกไปจากตาราง
   */
  removeItem(index: number) {
    this.selectedItems.splice(index, 1);
    this.calculateTotals();
  }

  /**
   * คำนวณยอดเงิน Subtotal, VAT และยอดเงินรวมสุทธิ (สอดคล้อง backend ConfirmPaymentRegisterGroupAsync)
   */
  calculateTotals() {
    let subTotal = 0;
    let vatAmount = 0;
    let total = 0;

    for (const item of this.selectedItems) {
      const amounts = this.lineAmounts(item);
      subTotal += amounts.subTotal;
      vatAmount += amounts.vatAmount;
      total += amounts.total;
    }

    this.subTotal = subTotal;
    this.vatAmount = vatAmount;
    this.total = total;
  }

  lineAmounts(item: CityParkingRegisterDetailMemberGroupTable): VatLineAmounts {
    return calculateVatLineAmounts(
      item.fee ?? 0,
      item.memberVehiclesPayloads?.length ?? 0,
      item.vat ?? false,
      item.vatIncluded ?? false,
      this.vatSetting.ratePercent(),
    );
  }

  unitAmounts(item: CityParkingRegisterDetailMemberGroupTable): VatLineAmounts {
    return calculateVatLineAmounts(
      item.fee ?? 0,
      1,
      item.vat ?? false,
      item.vatIncluded ?? false,
      this.vatSetting.ratePercent(),
    );
  }

  /**
   * ยอดราคารวมไม่รวมภาษี
   */
  get totalPrice(): number {
    return this.subTotal;
  }

  /**
   * ล้างข้อมูลทั้งหมดในฟอร์มเพื่อเริ่มกรอกใหม่
   */
  resetForm() {
    this.selectedLocationId = this.siteId > 0 ? this.siteId : null;
    this.selectedClientId = 0;
    this.draftClientName = '';
    this.selectedCustomerId = null;
    this.customerSearchText = '';
    this.isCustomerDropdownOpen = false;
    this.previousLocationId = this.siteId > 0 ? this.siteId : null;
    this.previousCustomerId = null;
    this.mode = 'new';
    this.selectedVehicleTypeId = null;
    this.selectedPaymentTypeId = null;
    this.selectedBankId = null;
    this.selectedItems = [];
    this.subTotal = 0;
    this.vatAmount = 0;
    this.total = 0;
    this.description = '';
    this.isAddModalOpen = false;
    this.taxInvoiceNeeded = false;
    this.selectedInvoiceId = null;
    this.isFulltaxModalOpen = false;
    this.isConfirmModalOpen = false;
    this.isLoading = false;
  }

  /**
   * ทำการกดยืนยันเพื่อบันทึกข้อมูล โดยตรวจสอบเงื่อนไขที่จำเป็น
   */
  submit() {
    if (this.checkValue()) {
      return;
    }

    if (this.taxInvoiceNeeded && !this.selectedTaxAddressId) {
      this.toast.error('กรุณาเลือกที่อยู่ออกใบกำกับภาษี');
      return;
    }

    if (this.selectedItems.length === 0) {
      this.toast.error('กรุณาเพิ่มรายการจอดรถอย่างน้อย 1 รายการ');
      return;
    }

    if (!this.menuId) {
      this.toast.error('ไม่พบ menuId ของหน้านี้ กรุณา refresh หรือเปิดจากเมนูลงทะเบียนจอดรถ');
      return;
    }

    this.cityparkingRegisterMemberGroupPayload = {
      id: this.isEditMode ? this.groupId : 0,
      name: '',
      description: this.description ?? '',
      siteId: this.selectedLocationId || 0,
      clientId: this.selectedClientId,
      cpCustomerId: this.selectedCustomerId || 0,
      fullTax: this.taxInvoiceNeeded,
      registerMemberType: this.mode === 'new' ? 1 : 2,
      cpInvoiceId: this.selectedInvoiceId || 0,
      cpAddressId: this.taxInvoiceNeeded ? this.selectedTaxAddressId || 0 : 0,
      userId: this.userId,
      menuId: this.menuId,
      vehicleTypeId: this.selectedVehicleTypeId || 0,
      receiptBase64: '',
      feeTypeId: this.selectedPaymentTypeId || 0,
      bankId: this.selectedPaymentTypeId === 3 ? this.selectedBankId || 0 : 0,
      delayed: this.delayed,
      dueDate: this.dueDate?.trim() ? this.dueDate : null,
      receiptNo: this.receiptNo ?? '',
      cityParkingRegisterDetailMemberGroupPayloads: this.selectedItems.map(
        (item) => ({
          groupId: item.groupId,
          userId: item.userId,
          cpUserId: item.cpUserId,
          uid: item.uid ?? '',
          phone: item.phone ?? '',
          fee: item.fee,
          memberStartDate: item.memberStartDate,
          feeId: item.feeId,
          siteId: item.siteId || this.selectedLocationId || 0,
          cardId: item.cardId,
          subScriptionId: item.subScriptionId,
          memberVehiclesPayloads: (item.memberVehiclesPayloads ?? []).map((v) => ({
            vehicleId: v.vehicleId,
            license: v.license ?? '',
            province: v.province ?? '',
            vehicleTypeId: String(v.vehicleTypeId ?? this.selectedVehicleTypeId ?? 0),
          })),
        }),
      ),
    };

    this.isConfirmModalOpen = true;
  }

  /**
   * ยืนยันการบันทึกรายการใน Confirm Modal เพื่อส่งไปบันทึกที่ API
   */
  confirmSave() {
    this.isConfirmModalOpen = false;
    this.sentSaveCityparkingRegisterMemberGroup();
  }

  /**
   * ปิด Confirm Modal
   */
  closeConfirmModal() {
    this.isConfirmModalOpen = false;
  }

  /**
   * ตรวจสอบว่าวันครบกำหนดชำระเลยกำหนดจากปัจจุบันเพื่อต้องการเลขใบเสร็จชำระเงินหรือไม่
   */
  checkDueDateDelay() {
    const todayISO = new Date().toISOString().split('T')[0];
    const today = new Date(todayISO);
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today.getTime() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const dueDate = new Date(this.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    if (dueDate < yesterday && this.delayed) {
      return true;
    }
    return false;
  }

  // ================= API Functions =================

  /**
   * โหลดข้อมูลพื้นฐานสถานที่ในระบบ
   */
  loadMasterData() {
    this.isLoading = true;
    this.getProviders();
  }

  /**
   * ดึงรายการใบแจ้งหนี้ตามสถานที่และลูกค้าที่เลือก
   */
  getInvoice() {
    this.apiMasterService
      .getInvoiceList(
        this.selectedLocationId || 0,
        this.selectedCustomerId || 0,
      )
      .subscribe({
        next: (res: InvoiceResponse[]) => {
          this.invoices = res;
          if (this.pendingInvoiceId && this.pendingInvoiceId > 0) {
            this.selectedInvoiceId = this.pendingInvoiceId;
            this.pendingInvoiceId = null;
          } else if (!this.isEditMode) {
            this.selectedInvoiceId = 0;
          }
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  /**
   * ดึงข้อมูลสถานที่สำหรับการลงทะเบียน
   */
  getProviders() {
    this.apiFormService.getProviders().subscribe({
      next: (res) => {
        this.locations = res;
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  /**
   * ดึงข้อมูลที่อยู่ออกใบกำกับภาษีของลูกค้า
   */
  getTax() {
    this.apiFormService
      .getCityAddressForSelectByCustomerId(this.selectedCustomerId || 0)
      .subscribe({
        next: (res) => {
          this.taxs = res;
          if (this.isEditMode && this.selectedTaxAddressId > 0) {
            const exists = res.some((t) => t.id === this.selectedTaxAddressId);
            if (!exists && res.length > 0) {
              this.selectedTaxAddressId = res[0].id;
            }
          } else if (res.length > 0) {
            this.selectedTaxAddressId = res[0].id;
          }
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  /**
   * ดึงข้อมูลประเภทรถจาก API สำหรับตัวเลือกในหน้าจอ
   */
  getVehicleTypes() {
    this.apiFormService.getParkingVehicleType().subscribe({
      next: (res) => {
        this.carTypes = res
          .map((item: any) => ({
            id: item.id,
            typeName: item.typeName,
            icon: item.id === 1 ? 'directions_car' : 'two_wheeler',
          }))
          .sort((a, b) => a.id - b.id);
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  /**
   * ดึงข้อมูลประเภทค่าธรรมเนียม / วิธีชำระเงิน สำหรับเลือกชำระ
   */
  getPaymentTypes() {
    this.apiFormService.getFeeType().subscribe({
      next: (res) => {
        this.paymentTypes = res;
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  /**
   * ดึงข้อมูลลูกค้าทั้งหมดในระบบสำหรับหน้าลงทะเบียนจอดรถ
   */
  getCityParkingCustomerList() {
    this.isLoading = true;
    this.apiFormService.getCityParkingCustomerList(this.selectedLocationId || this.siteId).subscribe({
      next: (res) => {
        this.customers = res;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูลลูกค้าได้');
        this.isLoading = false;
      },
    });
  }

  /**
   * ส่งข้อมูลบันทึกการลงทะเบียนไปยัง Server
   */
  sentSaveCityparkingRegisterMemberGroup() {
    this.isLoading = true;
    const request$ = this.isEditMode
      ? this.apiService.updateRegisterMemberGroupDraft(
          this.cityparkingRegisterMemberGroupPayload,
        )
      : this.apiService.sentSaveCityparkingRegisterMemberGroup(
          this.cityparkingRegisterMemberGroupPayload,
        );

    request$.subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isSuccess === false) {
          this.toast.error(res.message);
        } else {
          this.toast.success(this.isEditMode ? 'แก้ไขฉบับร่างสำเร็จ' : 'บันทึกข้อมูลสําเร็จ');
          this.resetForm();
          this.onSaved();
        }
      },
      error: (err) => {
        console.error(err);
        this.toast.error(this.isEditMode ? 'ไม่สามารถแก้ไขฉบับร่างได้' : 'ไม่สามารถบันทึกข้อมูลได้');
        this.isLoading = false;
      },
    });
  }

  /**
   * โหลดฉบับร่างเพื่อแก้ไข
   */
  private loadDraftForEdit(): void {
    this.isLoading = true;
    this.apiService.getCityParkingRegisterMemberGroupById(this.groupId).subscribe({
      next: (res: CityParkingRegisterGroupByIdResponse) => {
        if (!res || res.status !== 1) {
          this.toast.error('แก้ไขได้เฉพาะฉบับร่างเท่านั้น');
          this.isLoading = false;
          this.onClose();
          return;
        }

        this.description = res.description ?? '';
        this.selectedLocationId = res.siteId || this.siteId || null;
        this.previousLocationId = this.selectedLocationId;
        this.loadBanks();
        this.selectedClientId = res.clientId ?? 0;
        this.draftClientName = res.clientName ?? '';
        this.selectedCustomerId = res.cpCustomerId || null;
        this.previousCustomerId = this.selectedCustomerId;
        this.mode = res.registerType === 2 ? 'renew' : 'new';
        this.selectedPaymentTypeId = res.feeTypeId || null;
        this.selectedBankId = res.bankAccountId || null;
        this.delayed = !!res.delayed;
        this.dueDate = this.toDateInputValue(res.dueDate) || this.today;
        this.receiptNo = res.receiptNo ?? res.receiptId ?? '';
        this.taxInvoiceNeeded = !!res.fullTax;
        this.selectedTaxAddressId = res.cpAddressInvoiceId || 0;
        this.pendingInvoiceId = res.cpInvoiceHId ?? null;
        this.selectedInvoiceId = res.cpInvoiceHId ?? 0;
        this.selectedVehicleTypeId = res.vehicleTypeId || null;

        this.selectedItems = (res.cityParkingRegisterDetailMemberGroupResponse ?? []).map(
          (member) => ({
            groupId: this.groupId,
            userId: this.userId,
            cpUserId: 0,
            uid: member.uid ?? '',
            phone: member.phone ?? '',
            fee: member.fee ?? 0,
            memberStartDate: this.toDateInputValue(member.memberStartDate) || this.today,
            feeId: member.feeId ?? 0,
            feeName: member.feeName ?? '',
            siteId: this.selectedLocationId || 0,
            subScriptionId: member.subscriptionId ?? 0,
            vat: member.vat ?? false,
            vatIncluded: member.vatIncluded ?? false,
            memberVehiclesPayloads: (member.memberVehicles ?? []).map((v) => ({
              vehicleId: v.vehicleId ?? 0,
              license: v.license ?? '',
              province: v.province ?? '',
              vehicleTypeId: String(v.vehicleTypeId ?? this.selectedVehicleTypeId ?? 0),
            })),
          }),
        );

        const selectedCustomer = this.customers.find((c) => c.id === this.selectedCustomerId);
        if (selectedCustomer) {
          this.customerSearchText = `${selectedCustomer.name} (${selectedCustomer.phone})`;
        } else if (res.customerName) {
          this.customerSearchText = res.customerName;
        }

        this.calculateTotals();
        this.loadClients();

        if (this.selectedCustomerId) {
          this.getTax();
        }
        if (this.selectedLocationId && this.selectedCustomerId) {
          this.getInvoice();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดฉบับร่างได้');
        this.isLoading = false;
        this.onClose();
      },
    });
  }

  private toDateInputValue(value: string | Date | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const raw = String(value);
      return raw.length >= 10 ? raw.slice(0, 10) : '';
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /** บัญชีรับโอนของลาน (city_parking_bank_account) */
  private loadBanks(): void {
    const siteId = this.selectedLocationId || this.siteId;
    if (!siteId) {
      this.banks = [];
      this.selectedBankId = null;
      return;
    }

    this.apiFormService.getBank(siteId).subscribe({
      next: (res) => {
        this.banks = res ?? [];
        if (this.selectedBankId && !this.banks.some((b) => b.id === this.selectedBankId)) {
          this.selectedBankId = null;
        }
      },
      error: () => {
        this.banks = [];
      },
    });
  }
}
